import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import {
	CertificateEntry,
	ConnectorKeyStore,
	ConnectorKeyStoreInterface,
	KeystoreSSL,
	SSLKeystoreFormat
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import CertificateDialogFields from "../field/CertificateDialogFields";
import {Password} from "primereact/password";
import FileInput, {File} from "../../../shared/components/FileInput";
import {InputText} from "primereact/inputtext";
import Administration from "../../session/Administration";
import {KeystoreType} from "@softvision/webpdf-wsclient-9-typescript/lib/session/Administration";
import {KeystoreViewer} from "../../../shared/components/KeystoreViewer";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import Info, {InfoType} from "../../../shared/components/Info";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import DomHelper from "../../../shared/helper/DomHelper";

export interface ConnectionCertificateDialogProperties extends AdminDialogProperties<KeystoreSSL> {
	onError: (exception: Error) => Promise<boolean>,
	submit: (value?: KeystoreSSL) => Promise<void>,
}

interface ConnectionCertificateDialogStatus extends AdminDialogState<KeystoreSSL> {
	keystoreFile: File;
	keystoreSet: boolean;
	certificates?: Array<CertificateEntry>;
	keystoreName: string;
}

export class ConnectionCertificateDialogDefinition extends AdminDialog<KeystoreSSL,
	ConnectionCertificateDialogProperties, ConnectionCertificateDialogStatus> {
	private readonly DEFAULT_FILENAME: string = "ssl";

	constructor(props: Readonly<ConnectionCertificateDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			keystoreFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			keystoreSet: false,
			keystoreName: this.DEFAULT_FILENAME
		};
	}

	private renderCertificateContents(): ReactNode {
		if (!this.state.keystoreSet) {
			return null;
		}

		return (
			<>
				<div className="p-col-12">
					<KeystoreViewer {...this.props} certificates={this.state.certificates}/>
				</div>
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("certificate_edit.new", {ns: "dialog"})}
							className="dialog-button"
							onClick={() => {
								this.onChange(CertificateDialogFields.PASSWORD, "");
								this.onChange(CertificateDialogFields.ALIAS, "");

								this.setState({
									keystoreSet: false
								});
							}}/>
				</div>
			</>
		);
	}

	public renderFormContents(): ReactNode {
		let parameter: KeystoreSSL | undefined = this.state.parameter;

		if (!FunctionHelper.exists(parameter) || this.state.keystoreSet) {
			return null;
		}

		return (
			<fieldset className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-certificate-file">
						<Translate {...this.props} data="certificate_edit.certificate" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={KeystoreSSL.getFileDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<FileInput inputId="control-certificate-file" value={this.state.keystoreFile.value}
								   accept={".jks, .pem, .p12, .pfx"}
								   filename={this.state.keystoreFile.name}
								   onChange={(files) => this.onChange(
									   CertificateDialogFields.CERTIFICATE, files[0]
								   )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-certificate-alias">
						<Translate {...this.props} data="certificate_edit.alias" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={KeystoreSSL.getAliasDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<InputText id="control-certificate-alias"
								   value={FunctionHelper.applyValue(
									   parameter?.alias, KeystoreSSL.getAliasDefault()
								   )}
								   onInput={(e) => this.onChange(
									   CertificateDialogFields.ALIAS, e.currentTarget.value
								   )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-certificate-password">
						<Translate {...this.props} data="certificate_edit.password" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={KeystoreSSL.getPasswordDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<Password inputId="control-certificate-password" feedback={false} toggleMask={true}
								  value={FunctionHelper.applyValue(
									  parameter?.password, KeystoreSSL.getPasswordDefault()
								  )}
								  onChange={(e) => this.onChange(
									  CertificateDialogFields.PASSWORD, e.currentTarget.value
								  )}/>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["connection-certificate-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					resizable={false} appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.connection.cerificate"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderFormContents()}
					{this.renderCertificateContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-certificate"}/>
					<Translate {...this.props} data="certificate_edit.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: CertificateDialogFields, value: any): void {
		let parameter: KeystoreSSL | undefined = this.state.parameter as KeystoreSSL;

		switch (field) {
			case CertificateDialogFields.CERTIFICATE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let keystoreFile: File = value;

				if ([".p12", ".pfx"].some((type) => keystoreFile.name.toLowerCase().includes(type))) {
					parameter.type = SSLKeystoreFormat.PKCS12;
				} else {
					parameter.type = SSLKeystoreFormat.JKS;
				}

				parameter.file = this.state.keystoreName;

				this.setState({
					keystoreFile: keystoreFile
				});
				break;
			case CertificateDialogFields.ALIAS:
				parameter.alias = value;
				break;
			case CertificateDialogFields.PASSWORD:
				parameter.password = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected isValid(): boolean {
		if (this.state.keystoreFile.value === "") {
			return false;
		}

		return super.isValid();
	}

	protected hasChanges(): boolean {
		let result: boolean = super.hasChanges();

		if (this.state.keystoreFile.value !== "") {
			result = true;
		}

		return result;
	}

	private async isKeystoreSet(): Promise<void> {
		let administration: Administration = this.props.context.session.getAdministration();
		let parameter: KeystoreSSL = this.state.parameter as KeystoreSSL;

		let connectorKeyStores: {
			[key: string]: ConnectorKeyStore;
		} = await administration.getKeystore(KeystoreType.Connector) as {
			[key: string]: ConnectorKeyStore;
		};

		this.setState({
			keystoreSet: FunctionHelper.applyValue(
				connectorKeyStores[parameter.file!]?.certificates, ConnectorKeyStore.getCertificatesDefault()
			).length > 0
		});
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();
		await this.reset();

		let administration: Administration = this.props.context.session.getAdministration();
		let parameter: KeystoreSSL = this.state.parameter as KeystoreSSL;
		parameter.password = KeystoreSSL.getPasswordDefault();
		parameter.alias = KeystoreSSL.getAliasDefault();

		let connectorKeyStores: {
			[key: string]: ConnectorKeyStore;
		} = await administration.getKeystore(KeystoreType.Connector) as {
			[key: string]: ConnectorKeyStore;
		};
		let certificates: Array<CertificateEntry> = FunctionHelper.applyValue(
			connectorKeyStores[parameter.file!]?.certificates, ConnectorKeyStore.getCertificatesDefault()
		);

		this.setState({
			keystoreName: this.DEFAULT_FILENAME + DomHelper.generateUUID(),
			parameter: parameter,
			certificates: certificates
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let parameter: KeystoreSSL = this.state.parameter as KeystoreSSL;
		let administration: Administration = this.props.context.session.getAdministration();
		let connectorKeyStores: {
			[key: string]: ConnectorKeyStore;
		} = await administration.getKeystore(KeystoreType.Connector) as {
			[key: string]: ConnectorKeyStore;
		};

		let tempConnectorKeyStores: any = {};
		for (let [key, value] of Object.entries(connectorKeyStores)) {
			tempConnectorKeyStores[key] = ConnectorKeyStore.fromJson(value);
		}

		connectorKeyStores[parameter.file!] = ConnectorKeyStore.fromJson({
			keyStoreContent: this.state.keystoreFile.value
		} as ConnectorKeyStoreInterface);

		try {
			administration.setKeystore(KeystoreType.Connector, connectorKeyStores);
			await this.props.submit(parameter.clone());

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}

			// reset keystores
			administration.setKeystore(KeystoreType.Connector, tempConnectorKeyStores);
			return;
		}

		this.hide();
	}

	protected hide() {
		this.setState({
			keystoreFile: {
				name: "",
				mimetype: "",
				value: ""
			}
		});

		super.hide();
	}

	protected async reset(): Promise<void> {
		await super.reset();
		await this.isKeystoreSet();

		let parameter: KeystoreSSL = this.state.parameter as KeystoreSSL;
		parameter.password = KeystoreSSL.getPasswordDefault();
		parameter.alias = KeystoreSSL.getAliasDefault();

		this.setState({
			keystoreFile: {
				name: "",
				mimetype: "",
				value: ""
			}
		});
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.CONNECTION_CERTIFICATE;
	}
}

const ConnectionCertificateDialog = React.memo(ConnectionCertificateDialogDefinition,
	(
		prevProps: Readonly<ConnectionCertificateDialogProperties>,
		nextProps: Readonly<ConnectionCertificateDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default ConnectionCertificateDialog;